/**
 * Phase 4+ — Audit Timeline Panel
 * 
 * Immutable, chronological audit timeline for a case.
 * Shows every decision, confirmation, and export event.
 * Viewable by compliance roles, exportable as PDF.
 */

import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  History,
  Shield,
  Download,
  FileText,
  Stethoscope,
  CheckCircle2,
  AlertTriangle,
  Package,
  Search,
  Lock,
  Eye,
  Settings,
  Mic,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AuditTimelineEvent, AuditTimelineEventType } from '@/lib/compliance/types';

interface AuditTimelinePanelProps {
  events: AuditTimelineEvent[];
  caseNumber?: string;
  isDemoMode: boolean;
  onExportPdf?: () => void;
  compact?: boolean;
}

const EVENT_ICONS: Partial<Record<AuditTimelineEventType, React.ReactNode>> = {
  authorization_created: <FileText className="h-3.5 w-3.5" />,
  subset_selected: <Settings className="h-3.5 w-3.5" />,
  surgeon_attestation: <Shield className="h-3.5 w-3.5" />,
  authorization_approved: <CheckCircle2 className="h-3.5 w-3.5" />,
  dictation_started: <Mic className="h-3.5 w-3.5" />,
  dictation_completed: <Mic className="h-3.5 w-3.5" />,
  dictation_signed: <Lock className="h-3.5 w-3.5" />,
  op_note_uploaded: <FileText className="h-3.5 w-3.5" />,
  analysis_started: <Search className="h-3.5 w-3.5" />,
  analysis_completed: <Search className="h-3.5 w-3.5" />,
  proof_map_evaluated: <Search className="h-3.5 w-3.5" />,
  cpt_confirmed_performed: <Stethoscope className="h-3.5 w-3.5" />,
  cpt_not_performed: <Stethoscope className="h-3.5 w-3.5" />,
  modifier_applied: <Settings className="h-3.5 w-3.5" />,
  modifier_overridden: <AlertTriangle className="h-3.5 w-3.5" />,
  export_gates_evaluated: <Shield className="h-3.5 w-3.5" />,
  export_generated: <Package className="h-3.5 w-3.5" />,
  override_applied: <AlertTriangle className="h-3.5 w-3.5" />,
  phi_accessed: <Eye className="h-3.5 w-3.5" />,
};

const EVENT_COLORS: Partial<Record<AuditTimelineEventType, string>> = {
  authorization_created: 'bg-primary/10 text-primary border-primary/30',
  surgeon_attestation: 'bg-success/10 text-success border-success/30',
  authorization_approved: 'bg-success/10 text-success border-success/30',
  dictation_signed: 'bg-success/10 text-success border-success/30',
  cpt_confirmed_performed: 'bg-success/10 text-success border-success/30',
  cpt_not_performed: 'bg-muted text-muted-foreground border-border',
  override_applied: 'bg-warning/10 text-warning border-warning/30',
  modifier_overridden: 'bg-warning/10 text-warning border-warning/30',
  export_generated: 'bg-primary/10 text-primary border-primary/30',
  export_failed: 'bg-destructive/10 text-destructive border-destructive/30',
  phi_accessed: 'bg-muted text-muted-foreground border-border',
};

const PHASE_LABELS: Record<string, string> = {
  authorization_created: 'Phase 1',
  subset_selected: 'Phase 1',
  surgeon_attestation: 'Phase 1',
  authorization_approved: 'Phase 1',
  dictation_started: 'Phase 2.5',
  dictation_completed: 'Phase 2.5',
  dictation_signed: 'Phase 2.5',
  op_note_uploaded: 'Phase 3',
  op_note_replaced: 'Phase 3',
  analysis_started: 'Phase 3',
  analysis_completed: 'Phase 3',
  proof_map_evaluated: 'Phase 3',
  clarification_generated: 'Phase 3',
  clarification_responded: 'Phase 3',
  cpt_confirmed_performed: 'Phase 3',
  cpt_not_performed: 'Phase 3',
  cpt_deferred: 'Phase 3',
  modifier_applied: 'Phase 3',
  modifier_overridden: 'Phase 3',
  sequencing_completed: 'Phase 3',
  export_gates_evaluated: 'Phase 4',
  export_generated: 'Phase 4',
  export_failed: 'Phase 4',
  export_copied: 'Phase 4',
  override_applied: 'Override',
  override_reverted: 'Override',
  case_state_changed: 'System',
  phi_accessed: 'Security',
};

type FilterPhase = 'all' | 'phase1' | 'phase2' | 'phase3' | 'phase4' | 'overrides' | 'security';

export function AuditTimelinePanel({
  events,
  caseNumber,
  isDemoMode,
  onExportPdf,
  compact = false,
}: AuditTimelinePanelProps) {
  const [filterPhase, setFilterPhase] = useState<FilterPhase>('all');

  const filteredEvents = useMemo(() => {
    if (filterPhase === 'all') return events;
    return events.filter(e => {
      const phase = PHASE_LABELS[e.eventType] || '';
      switch (filterPhase) {
        case 'phase1': return phase === 'Phase 1';
        case 'phase2': return phase === 'Phase 2.5';
        case 'phase3': return phase === 'Phase 3';
        case 'phase4': return phase === 'Phase 4';
        case 'overrides': return e.isOverride || phase === 'Override';
        case 'security': return e.phiAccessed || phase === 'Security';
        default: return true;
      }
    });
  }, [events, filterPhase]);

  const overrideCount = events.filter(e => e.isOverride).length;
  const phiAccessCount = events.filter(e => e.phiAccessed).length;

  return (
    <Card className="border-border">
      <CardHeader className={cn('pb-3', compact && 'py-3')}>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className={cn('flex items-center gap-2', compact ? 'text-sm' : 'text-base')}>
              <History className="h-4 w-4 text-primary" />
              Audit Timeline
              {isDemoMode && (
                <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                  Demo
                </Badge>
              )}
            </CardTitle>
            {!compact && (
              <CardDescription className="mt-1">
                Immutable, chronological record of all case decisions and actions
              </CardDescription>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {events.length} events
            </Badge>
            {overrideCount > 0 && (
              <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                {overrideCount} override{overrideCount > 1 ? 's' : ''}
              </Badge>
            )}
            {onExportPdf && (
              <Button variant="outline" size="sm" onClick={onExportPdf} className="gap-1 text-xs">
                <Download className="h-3 w-3" />
                PDF
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        {!compact && (
          <div className="flex items-center gap-2 mt-3">
            <Select value={filterPhase} onValueChange={(v) => setFilterPhase(v as FilterPhase)}>
              <SelectTrigger className="w-40 h-8 text-xs">
                <SelectValue placeholder="Filter phase" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="phase1">Phase 1 — Auth</SelectItem>
                <SelectItem value="phase2">Phase 2.5 — Dictation</SelectItem>
                <SelectItem value="phase3">Phase 3 — Reconciliation</SelectItem>
                <SelectItem value="phase4">Phase 4 — Export</SelectItem>
                <SelectItem value="overrides">Overrides Only</SelectItem>
                <SelectItem value="security">PHI/Security</SelectItem>
              </SelectContent>
            </Select>
            {phiAccessCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {phiAccessCount} PHI access event{phiAccessCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </CardHeader>

      <CardContent className={cn(compact && 'pt-0')}>
        <ScrollArea className={cn(compact ? 'max-h-[250px]' : 'max-h-[500px]')}>
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-[18px] top-0 bottom-0 w-px bg-border" />

            <div className="space-y-0">
              {filteredEvents.map((event, index) => {
                const icon = EVENT_ICONS[event.eventType] || <History className="h-3.5 w-3.5" />;
                const colorClass = EVENT_COLORS[event.eventType] || 'bg-muted text-muted-foreground border-border';
                const phaseLabel = PHASE_LABELS[event.eventType] || 'System';

                return (
                  <div key={event.id} className="relative flex gap-3 pb-4 pl-1">
                    {/* Timeline dot */}
                    <div className={cn(
                      'relative z-10 flex h-8 w-8 items-center justify-center rounded-full border shrink-0',
                      colorClass,
                    )}>
                      {icon}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium leading-tight">{event.description}</p>
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-muted-foreground">
                              {new Date(event.timestamp).toLocaleString()}
                            </span>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">{event.actor}</span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                              {phaseLabel}
                            </Badge>
                            {event.isOverride && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-warning/10 text-warning border-warning/30">
                                Override
                              </Badge>
                            )}
                            {event.phiAccessed && (
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 bg-muted">
                                PHI
                              </Badge>
                            )}
                            {event.cptCode && (
                              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-mono">
                                {event.cptCode}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filteredEvents.length === 0 && (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  No events match the current filter.
                </div>
              )}
            </div>
          </div>
        </ScrollArea>

        {/* Compliance disclaimer */}
        <div className="mt-4 p-3 bg-muted/30 rounded-lg border border-border text-xs text-muted-foreground flex items-start gap-2">
          <Lock className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <p>
            This audit timeline is immutable and read-only. All entries are append-only and cannot be modified or deleted.
            {isDemoMode && ' Demo mode — synthetic data shown for illustration.'}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
